import {
  Target,
  BarChart3,
  Sparkles,
  Handshake,
  Store,
  Rocket,
  School,
  Building2,
  HeartHandshake,
  UserRound,
  Users,
  BookOpen,
  Megaphone,
  Search,
  Share2,
  Wrench,
  Sprout,
  LineChart,
  Mail,
  Bot,
  PenTool,
  GraduationCap,
  Flag,
  Laptop,
  Clapperboard,
} from 'lucide-react';
import { img } from './site';

export const valueProps = [
  {
    icon: Target,
    title: 'Strategy Driven',
    text: 'Tailored digital solutions designed around your business goals.',
  },
  {
    icon: BarChart3,
    title: 'Measurable Impact',
    text: 'Data-informed marketing focused on meaningful business outcomes.',
  },
  {
    icon: Sparkles,
    title: 'Creative Excellence',
    text: 'Powerful ideas, compelling content, and distinctive brand experiences.',
  },
  {
    icon: Handshake,
    title: 'Your Growth Partner',
    text: 'A committed digital partner helping you build and grow with confidence.',
  },
];

export const whyUs = [
  'Multiple digital services under one roof',
  'Solutions tailored to business needs',
  'Creative and strategic thinking',
  'Marketing informed by data',
  'Practical digital expertise',
  'A focus on visibility, growth, and opportunities',
  'Professional communication and collaboration',
];

export const audiences = [
  {
    icon: Store,
    title: 'Small & Medium-Sized Businesses',
    text: 'Build a stronger online presence, attract customers, and compete with confidence.',
  },
  {
    icon: Rocket,
    title: 'Entrepreneurs & Startups',
    text: 'Launch with clarity — brand, website, and marketing foundations that scale with you.',
  },
  {
    icon: School,
    title: 'Schools & Educational Institutions',
    text: 'Reach parents and students, communicate your value, and equip learners with digital skills.',
  },
  {
    icon: Building2,
    title: 'Corporate Organizations',
    text: 'Strengthen visibility, support campaigns, and produce content at a professional standard.',
  },
  {
    icon: HeartHandshake,
    title: 'NGOs & Nonprofits',
    text: 'Tell your story powerfully and rally support through strategic digital communication.',
  },
  {
    icon: UserRound,
    title: 'Personal Brands & Professionals',
    text: 'Shape a credible, memorable digital identity that opens doors to new opportunities.',
  },
  {
    icon: Users,
    title: 'Individuals Seeking Digital Skills',
    text: 'Learn practical, in-demand digital skills through structured courses and trainings.',
  },
];

export const approach = [
  {
    num: '01',
    title: 'Understand',
    text: 'We learn about your business, goals, audience, and challenges.',
  },
  {
    num: '02',
    title: 'Strategize',
    text: 'We identify the right digital solutions and develop a practical approach.',
  },
  {
    num: '03',
    title: 'Execute',
    text: 'We bring the strategy to life through creative, marketing, and technology solutions.',
  },
  {
    num: '04',
    title: 'Optimize',
    text: 'We review performance, identify opportunities, and refine our approach where appropriate.',
  },
];

export const courses = [
  {
    slug: 'complete-digital-marketing-course',
    name: 'Complete Digital Marketing Course',
    price: '₦150,000',
    oldPrice: null,
    badge: 'Most Popular',
    featured: true,
    description:
      'Explore digital marketing skills across advertising, content, social media, SEO, and AI-assisted workflows — one complete learning path.',
    points: [
      'Advertising, content & social media',
      'SEO fundamentals and application',
      'AI-assisted marketing workflows',
      'Practical, real-world exercises',
    ],
  },
  {
    slug: 'google-ads-seo-mastery',
    name: 'Google Ads & SEO Mastery',
    price: '₦100,000',
    oldPrice: '₦150,000',
    badge: 'Current Offer',
    featured: false,
    description:
      'Learn the fundamentals and practical applications of search advertising and search engine optimization.',
    points: [
      'Search advertising fundamentals',
      'Keyword research & campaign structure',
      'On-page and technical SEO',
      'Measurement and optimization',
    ],
  },
  {
    slug: 'individual-courses',
    name: 'Individual Digital Marketing Courses',
    price: '₦50,000',
    priceSuffix: ' each',
    oldPrice: null,
    badge: 'Flexible',
    featured: false,
    description:
      'Explore and select individual courses at ₦50,000 each — including Ads Targeting & Conversion and AI Prompt Engineering.',
    points: [
      'Ads Targeting & Conversion',
      'AI Prompt Engineering',
      'Social Media Marketing',
      'Google Ads · SEO · and more',
    ],
  },
];

export const portfolio = [
  {
    id: 'p1',
    title: 'Social Media Relaunch',
    client: 'Retail Fashion Brand',
    category: 'Social Media',
    service: 'Social Media Management & Ads',
    overview:
      'A complete content reset — strategy, content calendar, creative direction, and managed Meta campaigns for a growing fashion retailer.',
    image: img('photo-1556761175-5973dc0f32e7'),
    imageAlt: 'Creative team reviewing a social media campaign on a presentation screen',
  },
  {
    id: 'p2',
    title: 'Search Visibility Sprint',
    client: 'Education Provider',
    category: 'SEO & Ads',
    service: 'Google Ads & SEO',
    overview:
      'Keyword research, technical SEO clean-up, and a focused Google Search campaign built around high-intent enrollment searches.',
    image: img('photo-1432888622747-4eb9a8efeb07'),
    imageAlt: 'Marketing analytics dashboard with search performance metrics',
  },
  {
    id: 'p3',
    title: 'Corporate Website Build',
    client: 'Professional Services Firm',
    category: 'Web Development',
    service: 'Website Development',
    overview:
      'A responsive, conversion-focused business website with clear service pages, fast load times, and an SEO-friendly structure.',
    image: img('photo-1467232004584-a241de8bcf5d'),
    imageAlt: 'Responsive website design sketched and built across devices',
  },
  {
    id: 'p4',
    title: 'Brand Identity System',
    client: 'Startup Founder',
    category: 'Branding',
    service: 'Graphics Design & Branding',
    overview:
      'Logo, color system, typography, and brand guidelines — a complete visual identity designed for consistency across every touchpoint.',
    image: img('photo-1561070791-2526d30994b5'),
    imageAlt: 'Brand identity presentation with logo, colors and stationery',
  },
  {
    id: 'p5',
    title: 'Promotional Video Series',
    client: 'NGO Campaign',
    category: 'Content',
    service: 'Content Production & Creation',
    overview:
      'Concept, scriptwriting, production, and voiceover for a short promotional video series supporting a community campaign.',
    image: img('photo-1600880292203-757bb62b4baf'),
    imageAlt: 'Production team collaborating on a promotional video shoot',
  },
  {
    id: 'p6',
    title: 'Digital Skills Workshop',
    client: 'School Program',
    category: 'Training',
    service: 'Digital Trainings',
    overview:
      'A practical, hands-on digital marketing workshop equipping participants with applicable skills and tools.',
    image: img('photo-1543269865-cbf427effbad'),
    imageAlt: 'Young African students learning digital skills together on laptops',
  },
];

export const portfolioFilters = [
  'All',
  'Social Media',
  'SEO & Ads',
  'Web Development',
  'Branding',
  'Content',
  'Training',
];

// Verified client testimonials — brand logos from /public/brands.
export const testimonials = [
  {
    name: 'Smile 360 Dental Clinic',
    location: 'Abuja',
    service: 'SEO Ranking & Google Ads',
    quote:
      'TheBigHub helped strengthen our search visibility through SEO and Google Ads, making it easier for people actively searching for dental services to discover us online.',
    result: '376 booking appointments generated during the engagement.',
    logo: '/brands/smile360.png',
  },
  {
    name: 'IAUE',
    fullName: 'Ignatius Ajuru University of Education',
    location: 'Port Harcourt',
    service: 'Facebook Ads',
    quote:
      'TheBigHub helped us take our admission campaigns beyond ordinary awareness. Through targeted Facebook advertising, we reached prospective students and turned digital attention into actual admission registrations.',
    result: '1,313 admission registrations across two sessions.',
    logo: '/brands/iaue-sbs.png',
  },
  {
    name: 'Phydens Energy',
    location: 'Lagos',
    service: 'LinkedIn Ads & Email Marketing',
    quote:
      'TheBigHub brought greater structure to our B2B digital outreach through LinkedIn advertising and targeted email marketing, helping us communicate our services more strategically to relevant business audiences.',
    result: null,
    logo: '/brands/phydens.png',
  },
];

export const posts = [
  {
    slug: 'why-being-online-is-not-enough',
    title: 'Why Being Online Is Not Enough for Your Business',
    category: 'Digital Marketing',
    date: '2026-06-18',
    readingTime: '5 min read',
    author: 'TheBigHub Team',
    excerpt:
      'Having a page is not the same as having a presence. Here is what separates businesses that get discovered from those that get scrolled past.',
    image: img('photo-1551288049-bebda4e38f71'),
    imageAlt: 'Digital analytics dashboard showing audience growth charts',
    content: [
      'Most businesses today have some form of online presence — a social media page, a phone number on a directory, maybe a website built years ago. Yet many still struggle to attract customers online. The reason is simple: being online is a starting point, not a strategy.',
      'Visibility online is earned through consistency and intent. Search engines reward websites that are structured well, load quickly, and answer real questions. Social platforms reward brands that publish content people actually want to engage with. Paid advertising rewards campaigns that are targeted, measured, and refined.',
      'The businesses that win treat their digital presence as an asset that is actively managed. They know who they are trying to reach, what those people are searching for, and what action they want them to take next.',
      'Start with an honest audit: Can a stranger find you when they search for what you do? Does your content communicate your value in seconds? Is there a clear next step on every page? If the answer to any of these is no, that is your opportunity — and it is exactly the kind of gap a deliberate digital strategy is built to close.',
    ],
  },
  {
    slug: 'google-ads-vs-seo-where-to-invest',
    title: 'Google Ads vs SEO: Where Should You Invest First?',
    category: 'Google Ads',
    date: '2026-06-02',
    readingTime: '6 min read',
    author: 'TheBigHub Team',
    excerpt:
      'Paid search delivers speed; SEO compounds over time. Understanding how they work together helps you invest with confidence.',
    image: img('photo-1460925895917-afdab827c52f'),
    imageAlt: 'Laptop showing search advertising performance data',
    content: [
      'It is one of the most common questions businesses ask: should we spend on Google Ads or invest in SEO? The honest answer is that they solve different problems on different timelines.',
      'Google Ads puts you in front of high-intent searchers immediately. When someone searches for exactly what you sell, a well-structured campaign can place your business at the top of the results the same week it launches. You pay for that speed, and the moment you stop paying, the visibility stops.',
      'SEO is the opposite trade. It takes longer — technical fixes, content, and authority building compound over months — but the visibility you earn does not disappear when you pause spending. For most businesses, the smartest approach is sequenced: use ads to capture demand now while building organic visibility that lowers your cost per customer over time.',
      'Whichever you start with, the fundamentals are the same: know the exact phrases your customers search, build pages that answer those searches clearly, and track every inquiry back to its source. Decisions get easy when the data is honest.',
    ],
  },
  {
    slug: 'social-media-that-sells-without-shouting',
    title: 'Social Media That Sells Without Shouting',
    category: 'Social Media',
    date: '2026-05-20',
    readingTime: '4 min read',
    author: 'TheBigHub Team',
    excerpt:
      'The brands that win on social media are not the loudest. They are the most consistent, the most useful, and the most human.',
    image: img('photo-1611926653458-09294b3142bf'),
    imageAlt: 'Phone displaying a curated social media feed on a creative desk',
    content: [
      'Scroll any feed and you will see businesses shouting — discounts, urgency, noise. And yet the accounts people actually follow, remember, and buy from tend to do something quieter: they show up consistently with content that is useful, beautiful, or genuinely interesting.',
      'A strong social presence starts with clarity about who you are talking to and what they care about. From there, a content calendar turns good intentions into a rhythm your audience can rely on. Education, proof, personality, and promotion each have their place — the balance is what makes a feed feel like a brand rather than a billboard.',
      'Paid social amplifies what already works. When a post resonates organically, putting budget behind it extends its reach to the exact audiences you want. That loop — create, measure, amplify, refine — is what turns social media from a chore into a business asset.',
      'If your social media feels like shouting into the void, the fix is rarely more volume. It is more intention.',
    ],
  },
  {
    slug: 'your-website-is-your-hardest-working-employee',
    title: 'Your Website Is Your Hardest-Working Employee',
    category: 'Website Development',
    date: '2026-05-06',
    readingTime: '5 min read',
    author: 'TheBigHub Team',
    excerpt:
      'It works every hour of every day, meets every potential customer first, and never asks for leave. Is yours doing its job?',
    image: img('photo-1467232004584-a241de8bcf5d'),
    imageAlt: 'Designer planning a business website layout beside a laptop',
    content: [
      'Before a customer calls you, visits you, or sends a WhatsApp message, most of them have already met your business — through your website. That first impression happens without you in the room, which makes your website your hardest-working employee.',
      'A website that works does three things well. It communicates what you do and who it is for within seconds. It builds trust through professional design, clear copy, and proof. And it makes the next step obvious — a call, a form, a message, a purchase.',
      'Just as important is what happens under the surface. Pages that load quickly, layouts that adapt beautifully to phones, and a structure search engines can understand all determine whether people find you at all and whether they stay.',
      'If your website is more than a few years old, was built as an afterthought, or no longer reflects the quality of your work, it is quietly costing you opportunities. A focused redesign is often the highest-leverage investment a growing business can make.',
    ],
  },
  {
    slug: 'ai-tools-for-small-business-marketing',
    title: 'Practical AI Tools Every Small Business Should Be Using',
    category: 'AI & Innovation',
    date: '2026-04-22',
    readingTime: '6 min read',
    author: 'TheBigHub Team',
    excerpt:
      'AI will not replace your marketing judgment — but businesses that use it well are moving faster than those that do not.',
    image: img('photo-1626785774573-4b799315345d'),
    imageAlt: 'Creative professional using modern digital tools at a workstation',
    content: [
      'Artificial intelligence has moved from novelty to everyday utility. For small businesses, the opportunity is not about replacing people — it is about giving small teams the leverage of much larger ones.',
      'The most practical wins are in content and communication. AI assistants can draft first versions of social captions, product descriptions, and email campaigns that your team then refines with brand voice and judgment. What once took hours now takes minutes, which means consistency becomes affordable.',
      'Prompt engineering — the skill of asking AI tools well — is quickly becoming a core digital skill. The quality of what you get out depends heavily on the clarity of what you put in: context, audience, tone, and examples.',
      'The businesses that benefit most treat AI as a capable assistant, not an autopilot. Strategy, taste, and relationships remain human. Used deliberately, AI simply gives you more time and output to apply them.',
    ],
  },
  {
    slug: 'brand-consistency-builds-trust',
    title: 'Brand Consistency: The Quiet Trust Builder',
    category: 'Branding',
    date: '2026-04-08',
    readingTime: '4 min read',
    author: 'TheBigHub Team',
    excerpt:
      'Customers may never consciously notice consistent branding — but they feel it. And they trust it.',
    image: img('photo-1561070791-2526d30994b5'),
    imageAlt: 'Brand guidelines with consistent typography and color swatches',
    content: [
      'Think of the brands you trust most. Their colors, fonts, tone, and quality feel the same everywhere — their website, their social posts, their packaging, their proposals. That sameness is not boring; it is reassuring.',
      'Consistency signals professionalism. When every touchpoint looks and sounds like it came from the same deliberate place, customers conclude that the business behind it is organized, reliable, and serious. Inconsistency sends the opposite message, even when the product is excellent.',
      'The foundation is a simple brand system: a logo used correctly, a defined color palette, one or two typefaces, and a clear tone of voice. Documented in basic brand guidelines, these decisions stop being remade every time a flyer or post is created.',
      'For growing businesses, brand consistency is one of the cheapest forms of marketing. It compounds — every coherent impression reinforces the last, until your business becomes the obvious, trusted choice.',
    ],
  },
];

// Trusted brands — add new entries here as more logos are supplied.
export const brands = [
  { name: 'Phydens Energy Resources Ltd', logo: '/brands/phydens.png' },
  { name: 'TheBigPicture', logo: '/brands/thebigpicture.png' },
  { name: 'School of Basic Studies, IAUE', logo: '/brands/iaue-sbs.png' },
  { name: 'Smile 360', logo: '/brands/smile360.png' },
  { name: 'Oréa Botanica', logo: '/brands/orea-botanica.png' },
  { name: 'AutoPlus', logo: '/brands/autoplus.png' },
  { name: 'Organic Garden', logo: '/brands/organic-garden.png' },
  { name: 'Digitvant Microfinance Bank', logo: '/brands/digitvant.png' },
];

export const getPost = (slug) => posts.find((p) => p.slug === slug);

export const ebook = {
  title: 'TheBigHub Digital Marketing Playbook',
  copy: 'Learn Digital Marketing. Create Value. Earn More.',
  price: '₦3,999',
  bullets: [
    '12+ In-Demand Modules',
    'Practical Examples & Case Studies',
    'Premium Templates & Checklists',
    'AI Prompts, Tools & Resources',
    'Step-by-Step Strategies',
  ],
  image: '/ebook-preview-1.png',
  imageAlt: 'TheBigHub Digital Marketing Playbook — Money flows to value ebook flier',
  url: 'https://selar.com/7713365823',
};

// ─── Digital Marketing Playbook landing page content ─────────────────────────
export const playbookBenefits = [
  { icon: BookOpen, title: 'Understand the Fundamentals', text: 'Build a clear foundation in what digital marketing is and how it drives modern business.' },
  { icon: Megaphone, title: 'Learn How Online Advertising Works', text: 'See how paid campaigns reach the right people at the right time.' },
  { icon: Share2, title: 'Explore Social Media Strategies', text: 'Understand how brands grow audiences and turn attention into opportunity.' },
  { icon: Search, title: 'Understand Google Ads & SEO', text: 'Learn how businesses get discovered when customers are searching.' },
  { icon: Wrench, title: 'Discover Useful Digital Tools', text: 'Get familiar with the tools and AI prompts that make marketing work smarter.' },
  { icon: Sprout, title: 'Build a Foundation for Growth', text: 'Develop a base for further learning and practical application.' },
];

export const playbookModules = [
  { icon: BookOpen, title: 'Digital Marketing Fundamentals', text: 'The core concepts every digital marketer starts with.' },
  { icon: Megaphone, title: 'Social Media Marketing', text: 'Strategies for growing and engaging audiences on social platforms.' },
  { icon: Search, title: 'Search Engine Optimization', text: 'How websites earn visibility when people search.' },
  { icon: Target, title: 'Google Ads', text: 'How search advertising captures high-intent customers.' },
  { icon: PenTool, title: 'Content Creation', text: 'Creating content that communicates value and builds trust.' },
  { icon: Mail, title: 'Email Marketing', text: 'Reaching and nurturing audiences directly in their inbox.' },
  { icon: Bot, title: 'AI Tools & Prompts', text: 'Using AI assistants and prompts to work faster and smarter.' },
  { icon: LineChart, title: 'Digital Marketing Strategy', text: 'Bringing the pieces together into a plan that makes sense.' },
];

export const playbookAudiences = [
  { icon: GraduationCap, title: 'For Students & Graduates', text: 'Start developing practical digital knowledge while preparing for opportunities beyond the classroom.' },
  { icon: Flag, title: 'For NYSC Members', text: 'Make your service year an opportunity to explore a valuable digital skill.' },
  { icon: Laptop, title: 'For Aspiring Freelancers', text: 'Build foundational knowledge in the marketing skills businesses use every day.' },
  { icon: Store, title: 'For Business Owners', text: 'Understand the principles behind promoting your products and services online.' },
  { icon: Clapperboard, title: 'For Content Creators', text: 'Go beyond posting content and begin understanding the strategies behind digital visibility.' },
];

export const playbookFaqs = [
  { q: 'How much does the eBook cost?', a: '₦3,999, paid once. No subscriptions, no hidden charges.' },
  { q: 'How do I pay?', a: 'Click any Buy Now button, copy the displayed First Bank account number, and make your transfer from your banking app.' },
  { q: 'How do I receive the eBook?', a: "After payment, click \"I've Paid — Confirm on WhatsApp\", send your receipt, and receive your download link once your transfer has been verified." },
  { q: 'Can I read it on my smartphone?', a: 'Yes. The PDF can be opened on compatible smartphones, tablets, and computers.' },
  { q: 'Is it a physical book?', a: 'No. It is a digital eBook delivered to you as a downloadable PDF.' },
  { q: 'Will I become a digital marketing expert immediately?', a: 'No. The eBook is a learning resource intended to help readers build foundational knowledge and develop their understanding through study and practice.' },
];

// Upload sample pages from the real eBook to /public/ebook-preview-*.jpg and list them here.
export const playbookPreviews = [
  { image: '/ebook-preview-1.png', label: 'The Playbook — Overview' },
  { image: '/ebook-preview-2.png', label: 'Modules & Premium Bonuses' },
];
